import React, { useEffect, useState } from "react";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import BlogImage from "components/customImages/BlogImage";
import Button from "components/ui_components/button";
import { BASE_URL } from "helpers/constants/routes";

function Index() {
  const router = useRouter();
  const { locale } = router;
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setOrigin(window?.location?.origin);
    }
  }, []);
  useEffect(() => {
    document.documentElement.dir = "ltr";
  }, [locale]);
  const currentPath = router?.asPath;

  return (
    <>
      <Head>
        <title>{"Quelle est la meilleure méthode de dressage pour les chiens ? | Yawakit"}</title>
        <meta
          name="description"
          content="Découvrez les meilleures techniques pour dresser votre animal de compagnie. Conseils et astuces efficaces pour une éducation réussie de votre chien ou chat."
        />
        <link rel="alternate" hrefLang="fr" href={`${origin}${currentPath}`} />
        <link rel="alternate" hrefLang="ar" href={`${origin}/ar${currentPath}`} />
      </Head>

      <div className="flex flex-col  w-full py-10 justify-center">
        <div className="max-w-[680px] m-auto px-6">
          <h1 className="font-bold text-md lg:text-lg  mb-10 text-center ">{`Les meilleures techniques pour dresser votre animal de compagnie`}</h1>
          <time dateTime="20-06-2023">Juin 6, 2023</time>
          <p className="text-justify">
            {`Les animaux de compagnie sont parmi les plus belles choses que la vie peut offrir. Leur présence apporte joie et réconfort au quotidien. Cependant, pour que cette relation soit encore plus agréable,`}{" "}
            <span className="font-bold"> {`un bon dressage est essentiel.`} </span>{" "}
            {`Dans cet article nous vous proposons de découvrir les techniques les plus efficaces pour éduquer votre compagnon à quatre pattes. Grâce à nos conseils, vous apprendrez comment renforcer votre lien avec votre animal tout en favorisant son bien-être et son obéissance. `}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="shiba-inu-dog-taking-walk.jpg"
              alt="Techniques de dressage"
              className="object-contain"
              imageUrl="shiba-inu-dog-taking-walk.jpg"
            />
          </div>
          <br />
          <h2 className="font-bold text-md lg:text-lg m-5">{`Commencer le dressage dès le plus jeune âge`}</h2>
          <p className="text-justify">
            {`Il est important de commencer le dressage de votre animal de compagnie dès son plus jeune âge. Les premières semaines de la vie d'un animal sont`}{" "}
            <span className="font-bold">{`une période d'apprentissage intensif`} </span>{" "}
            {`où il est particulièrement`} <span className="font-bold"> {`réceptif`} </span>{" "}
            {`aux nouvelles expériences et aux nouvelles informations.
`}
          </p>
          <br />
          <p className="text-justify">
            {`En débutant l'éducation tôt, vous posez les bases d'un comportement positif qui perdurera tout au long de sa vie. Un `}{" "}
            <span className="font-bold"> {`chiot`} </span> {`ou un`} <span>{`chaton`} </span>{" "}
            {` bien dressé`} <span className="font-bold">{`apprend rapidement`} </span>{" "}
            {`les règles de la maison, comme la propreté, l'obéissance aux commandes de base, et les interactions sociales appropriées. Cela réduit les risques de développer des comportements indésirables, tels que l'agressivité, l'anxiété de séparation ou les dégâts matériels. De plus, un dressage précoce permet de renforcer la confiance entre l'animal et son propriétaire, créant ainsi une relation solide et harmonieuse.`}{" "}
          </p>
          <br />
          <p className="text-justify">
            {" "}
            {`Les`} <span className="font-bold"> {`séances de dressage`} </span>{" "}
            {`offrent également une excellente opportunité de`}{" "}
            <span className="font-bold"> {`stimulation mentale,`} </span>{" "}
            {`essentielle au bien-être de votre animal. En investissant du temps et de l'effort dans le dressage dès le plus jeune âge, vous préparez votre compagnon à quatre pattes à mener une vie équilibrée et heureuse, tout en facilitant votre cohabitation quotidienne. `}{" "}
          </p>
          <br />
          <h2 className="font-bold text-md lg:text-lg m-5">{`Optez pour des séances de dressage régulières `}</h2>
          <p className="text-justify">
            {`Le dressage d'un animal de compagnie ne doit pas se limiter aux premières semaines ou mois de sa vie, mais doit être un processus continu tout au long de son existence. Les bonnes habitudes et les comportements souhaités doivent être renforcés régulièrement pour devenir des réflexes durables. Consacrer du temps à l'éducation de votre animal signifie lui offrir des séances de dressage fréquentes et cohérentes, adaptées à ses besoins et à son niveau de progression. `}{" "}
          </p>
          <br />
          <p className="text-justify">
            {`La persévérance est la clé ! En effet, il est important de répéter les séances et de les réviser régulièrement pour éviter que l'animal ne les oublie. De plus, la patience joue un rôle primordial dans ce processus. Chaque animal apprend à son propre rythme, et il est important de ne pas se décourager face à des progrès lents ou des régressions occasionnelles.`}{" "}
          </p>
          <br />
          <p className="text-justify">
            {" "}
            {`En étant constant et patient, vous aiderez votre compagnon à intégrer profondément les comportements souhaités. Le dressage continu est un investissement de temps et d'efforts qui portera ses fruits en cultivant une relation forte et positive avec votre animal de compagnie. `}{" "}
          </p>
          <div className="flex justify-center pt-5">
            <BlogImage
              imageName="sportive-dog-performing-lure-coursing-competition.jpg"
              alt="Dressage Chien"
              className="object-contain"
              imageUrl="sportive-dog-performing-lure-coursing-competition.jpg"
            />
          </div>
          <br />
          <h2 className="font-bold text-md lg:text-lg m-5">{`Motiver et récompenser votre animal de compagnie`}</h2>
          <p className="text-justify">
            {`Utiliser des récompenses et des friandises est une manière fantastique et amusante de dresser votre animal de compagnie. Imaginez le visage joyeux de votre chien ou le ronronnement satisfait de votre chat lorsqu'ils reçoivent une délicieuse friandise ou croquette pour avoir bien agi. Ces moments de plaisir ne sont pas seulement gratifiants pour eux, ils renforcent également le lien entre vous. Les récompenses, qu'elles soient comestibles ou sous forme de caresses et d'éloges, sont des outils puissants pour encourager les bons comportements. Elles motivent votre animal à répéter les actions qui lui valent ces récompenses. Et cela ne se limite pas à la nourriture ! Une caresse affectueuse ou des mots doux peuvent avoir un impact tout aussi fort. 
`}{" "}
          </p>
          <div className="flex justify-center m-8">
            <Button>
              <Link
                target="_blank"
                rel="noopener"
                href={`${BASE_URL}categories/animalerie?extraOptions=%7B%22sub_category%22%3A%7B%22name%22%3A%7B%22containsi%22%3A%22Ani+%3E+Aliments%22%7D%7D%7D`}
                passHref
                legacyBehavior
              >
                {`Découvrez les meilleures nourritures pour votre animal !`}
              </Link>
            </Button>
          </div>
          <p className="italic text-justify">
            {`Nb : il est important de bien choisir les friandises. Optez pour des petites bouchées savoureuses qui peuvent être données fréquemment sans risquer d'excès. Variez les types de récompenses pour maintenir l'intérêt de votre animal et éviter qu'il ne se lasse.`}{" "}
          </p>
          <br />
          <h3> {`Ne soyez pas violent `} </h3>
          <p className="text-justify">
            {`Il est essentiel de rappeler que la`}{" "}
            <span className="font-bold">
              {`violence n'a pas sa place dans le dressage des animaux de compagnie.`}{" "}
            </span>{" "}
            {`Adopter une approche douce et bienveillante renforce la confiance et le respect mutuel entre vous et votre animal. En choisissant la voie de la compassion, vous créez un environnement propice à l'apprentissage et à l'épanouissement de votre fidèle compagnon. `}{" "}
          </p>
          <br />
          <p className="text-justify">
            {`Nous espérons que vous parvenez à`}{" "}
            <span className="font-bold">{`appliquer ces techniques de dressage`} </span>{" "}
            {`avec succès et à créer une relation harmonieuse et enrichissante avec votre compagnon à quatre pattes. Souvenez-vous toujours que la patience, la cohérence et la bienveillance sont les clés d'un dressage réussi, et que chaque moment passé à éduquer votre animal renforce le lien spécial qui vous unit. `}{" "}
          </p>
          <br />
          <p className="text-justify">
            {" "}
            {`Si vous cherchez des accessoires et des jouets pour gâter votre fidèle compagnon, nous vous invitons à découvrir notre sélection dans de`}{" "}
            <a
              href={`${BASE_URL}categories/animalerie`}
              className="underline text-blue-600  visited:text-[#0369a1]"
            >
              {" "}
              {`meilleures animaleries`}{" "}
            </a>{" "}
            {`en Tunisie.`}{" "}
          </p>
          <p className="text-justify">
            {`Offrez-lui des`} <span className="font-bold"> {`friandises savoureuses,`} </span>{" "}
            {`des`} <span className="font-bold"> {`jouets interactifs`} </span>{" "}
            {`et tout ce dont il a besoin pour s'épanouir pleinement, en quelques clics: `}{" "}
          </p>
          <div className="flex justify-center m-8">
            <Button>
              <Link
                target="_blank"
                rel="noopener"
                href={`${BASE_URL}categories/animalerie`}
                passHref
                legacyBehavior
              >
                {`Trouvez la meilleur animalerie en Tunisie`}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["layout", "categories", "signup_promotion"])),
    },
  };
}

export default Index;
